import { useQuery } from '@tanstack/react-query'
import { performanceApi } from '@/api/performance'
import type { PerformancePeriodSummary } from '@/types/performance'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, LayoutDashboard } from 'lucide-react'

const formatMoney = (v: number) => `¥${(v ?? 0).toFixed(2)}`

function PeriodCard({ title, data, loading }: { title: string; data?: PerformancePeriodSummary; loading: boolean }) {
  const vipPromotion = data ? (data.total_promotion - data.new_promotion) : 0
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">总业绩</span>
              <span className="text-2xl font-bold">{formatMoney(data?.total_promotion ?? 0)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">总订单数</span>
              <span className="text-lg font-medium">{data?.total_count ?? 0}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">新客户业绩</span>
              <span className="text-sm font-medium text-blue-600">{formatMoney(data?.new_promotion ?? 0)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">新客户订单数</span>
              <span className="text-sm font-medium">{data?.new_count ?? 0}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">复购业绩</span>
              <span className="text-sm font-medium text-green-600">{formatMoney(vipPromotion)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-muted-foreground">总金额</span>
              <span className="text-sm font-medium">{formatMoney(data?.total_amount ?? 0)}</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

export function PerformanceSummaryPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['performance-dashboard'],
    queryFn: () => performanceApi.getDashboard(),
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <LayoutDashboard className="h-8 w-8" />
          业绩看板
        </h1>
        <p className="text-muted-foreground mt-1">今/昨/本月/上月业绩汇总</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <PeriodCard title="今日" data={data?.today} loading={isLoading} />
        <PeriodCard title="昨日" data={data?.yesterday} loading={isLoading} />
        <PeriodCard title="本月" data={data?.this_month} loading={isLoading} />
        <PeriodCard title="上月" data={data?.last_month} loading={isLoading} />
      </div>
    </div>
  )
}
